  import React from "react";
  import { motion, AnimatePresence } from "framer-motion";
  import { Star } from "lucide-react";
  import ConfettiExplosion from "react-confetti-explosion";
  import { useReward } from "react-rewards";

  const ScoreAnimation = ({ score, onClose }) => {
    const [gifIndex] = React.useState(Math.random() < 0.5 ? 0 : 1);
    const [headingIndex] = React.useState(Math.floor(Math.random() * 3));
    const [subheadingIndex] = React.useState(Math.floor(Math.random() * 4));

    const getTheme = (score, gIndex, hIndex, sIndex) => {
      const getIcon = (src1, src2) => (
        <img
          src={gIndex === 0 ? src1 : src2}
          alt="Score Animation"
          className="w-[180px] h-[180px] sm:w-[220px] sm:h-[220px] object-contain drop-shadow-2xl"
        />
      );

      const getTitle = (...titles) =>
        titles[hIndex % titles.length].toUpperCase() + "!";
      const getSubtitle = (...subtitles) => subtitles[sIndex % subtitles.length];

      if (score === 100) {
        return {
          title: getTitle("Perfect Score", "Nailed It", "Spot On"),
          subtitle: getSubtitle(
            "You read everything very carefully",
            "Amazing reading from start to finish",
            "You did a perfect reading",
            "Great focus during the whole reading",
          ),
          stars: Math.floor(score / 20),
          primaryButtonBg: "bg-gradient-to-r from-[#FFCA28] to-[#FFA000]",
          primaryButtonText: "text-[#1C1A5E]",
          icon: getIcon("/gifs/Jump.gif", "/gifs/Sun.gif"),
        };
      } else if (score >= 91) {
        return {
          title: getTitle("Awesome", "Brilliant", "Almost Perfect"),
          subtitle: getSubtitle(
            "You did really great today",
            "Almost perfect reading well done",
            "Your reading was very strong",
            "Great effort while reading today",
          ),
          stars: Math.floor(score / 20),
          primaryButtonBg: "bg-gradient-to-r from-[#38BDF8] to-[#0284C7]",
          primaryButtonText: "text-white",
          icon: getIcon("/gifs/Melon.gif", "/gifs/Cat.gif"),
        };
      } else if (score >= 81) {
        return {
          title: getTitle("Great Job", "Excellent", "Impressive"),
          subtitle: getSubtitle(
            "You are reading very well",
            "Nice work on your reading",
            "Good reading keep it up",
            "You did a great job",
          ),
          stars: Math.floor(score / 20),
          primaryButtonBg: "bg-gradient-to-r from-[#34D399] to-[#059669]",
          primaryButtonText: "text-white",
          icon: getIcon("/gifs/Owl.gif", "/gifs/Dance.gif"),
        };
      } else if (score >= 51) {
        return {
          title: getTitle("Nice Try", "Good Effort", "Pretty Good"),
          subtitle: getSubtitle(
            "Nice try keep reading more",
            "Good effort keep practicing reading",
            "You are getting better each time",
            "Keep trying you are improving",
          ),
          stars: Math.floor(score / 20),
          primaryButtonBg: "bg-gradient-to-r from-[#FBBF24] to-[#D97706]",
          primaryButtonText: "text-white",
          icon: getIcon("/gifs/Rocket.gif", "/gifs/Egg.gif"),
        };
      } else {
        return {
          title: getTitle("Keep Practicing", "Keep Learning", "Practice More"),
          subtitle: getSubtitle(
            "Keep practicing your reading skills",
            "Try reading more and slowly",
            "Keep learning you can improve",
            "Practice more and try again",
          ),
          stars: Math.floor(score / 20),
          primaryButtonBg: "bg-gradient-to-r from-[#FB7185] to-[#BE123C]",
          primaryButtonText: "text-white",
          icon: getIcon("/gifs/Clap.gif", "/gifs/Bunny.gif"),
        };
      }
    };

    const theme = getTheme(score, gifIndex, headingIndex, subheadingIndex);

    // Setup reactive rewards for 100% (Left and Right cannons)
    const { reward: leftReward, isAnimating: isLeftAnimating } = useReward(
      "leftReward",
      "confetti",
      { angle: 45, spread: 50, elementCount: 30, startVelocity: 30 },
    );
    const { reward: rightReward, isAnimating: isRightAnimating } = useReward(
      "rightReward",
      "confetti",
      { angle: 135, spread: 50, elementCount: 30, startVelocity: 30 },
    );

    // Audio: runs once per score value — never loops
    React.useEffect(() => {
      const audioFile = score === 100 ? "/for_100.mp3" : "/for_rest.mp3";
      const audio = new Audio(audioFile);
      audio.loop = false;
      audio.play().catch((err) => console.warn("Audio playback failed:", err));
      return () => {
        audio.pause();
        audio.currentTime = 0;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [score]);

    // Confetti and rewards (Wait for user to click Continue Reading)
    React.useEffect(() => {
      if (score === 100) {
        let count = 0;

        leftReward();
        rightReward();
        count++;

        const interval = setInterval(() => {
          if (count >= 3) {
            clearInterval(interval);
            return;
          }

          leftReward();
          rightReward();
          count++;
        }, 1200);

        return () => clearInterval(interval);
      }
    }, [score]);

    return (
      <AnimatePresence>
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#363A50]/95 cursor-pointer"
          />

          {/* Confetti and Rewards for 100% */}
          {score === 100 && (
            <div className="absolute inset-0 pointer-events-none z-[10000] overflow-hidden">
              {/* Base Explosion */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <ConfettiExplosion
                  force={0.6}
                  duration={1800}
                  particleCount={80}
                  width={1200}
                />
              </div>

              {/* React Rewards Cannons */}
              <div className="absolute bottom-10 left-10">
                <span id="leftReward" />
              </div>
              <div className="absolute bottom-10 right-10">
                <span id="rightReward" />
              </div>
            </div>
          )}

          {/* Main Content Wrapper */}
          <motion.div
            initial={{ scale: 0.8, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.8, y: 30, opacity: 0 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="relative z-[10001] flex flex-col items-center w-full max-w-lg mt-8 pointer-events-none"
          >
            {/* Top Emoji/GIF Icon */}
            <motion.div
              initial={{ rotate: -10, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: "spring", delay: 0.2, damping: 15 }}
              className="mb-8 relative"
            >
              {theme.icon}
            </motion.div>

            {/* Text Headers */}
            <div className="text-center w-full mb-8">
              <h2
                className="text-5xl sm:text-6xl font-black italic tracking-wide text-white mb-3"
                style={{ textShadow: "0 4px 12px rgba(0,0,0,0.5)" }}
              >
                {theme.title}
              </h2>
              <p
                className="text-base sm:text-lg font-medium text-[#FCD34D] tracking-wide"
                style={{ textShadow: "0 2px 4px rgba(0,0,0,0.4)" }}
              >
                {theme.subtitle}
              </p>
            </div>

            {/* Stars Display (Replacing Score) */}
            <div className="flex gap-4 sm:gap-5 justify-center mb-12">
              {[1, 2, 3, 4, 5].map((starNum) => {
                const isActive = starNum <= theme.stars;
                return (
                  <motion.div
                    key={starNum}
                    initial={{ opacity: 0, scale: 0, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 0.4 + starNum * 0.1, type: "spring" }}
                  >
                    <Star
                      size={56}
                      className={
                        isActive
                          ? "text-[#FFCA28] fill-[#FFCA28]"
                          : "text-white/20 fill-white/10"
                      }
                      style={{
                        filter: isActive
                          ? "drop-shadow(0 0 16px rgba(250,204,21,0.6)) drop-shadow(0 4px 6px rgba(0,0,0,0.3))"
                          : "drop-shadow(0 4px 6px rgba(0,0,0,0.2))",
                      }}
                    />
                  </motion.div>
                );
              })}
            </div>

            {/* Buttons */}
            <div className="w-full flex justify-center px-6 pointer-events-auto">
              <button
                onClick={onClose}
                className={`w-full max-w-sm py-4 sm:py-5 text-sm sm:text-base font-bold uppercase tracking-wide rounded-2xl shadow-xl transition-all hover:brightness-110 active:translate-y-1 ${theme.primaryButtonBg} ${theme.primaryButtonText}`}
              >
                Continue Reading
              </button>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    );
  };

  export default ScoreAnimation;
